import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import { CmdLoader } from '@commander-services/gui-components';
import { BlockUserIcon } from '@commander-services/cmd-icon';
import { useIntl } from 'react-intl';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useLocation, useRoute } from 'wouter';
import Form from './Form';
import PermissionsService, { PERMISSION_PERSON } from '../../Services/PermissionsService';
import Detail from './Detail';
import { TABLE_PERSON } from '../../Services/TableService';
import { URL_EMPLOYEE } from '../../router/constants';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { CmdTable } from '../CmdTable/CmdTable';
import {
  EMPLOYEE_TABLE_NAME,
  EMPLOYEE_TABLE_SETTINGS_KEY,
  EmployeesTableTranslationKeys,
  CHECKBOX_KEY,
  EMPLOYEE_CUSTOMER,
  EMPLOYEE_USERNAME,
  REACTIVATE_USER,
  RESET_USER,
  ACTIVATE_USER,
  IS_INACTIVE,
  IS_PENDING,
  IS_ACTIVE,
  IS_BLOCKED,
} from './constants';
import { ACTIONS_KEY, CUSTOMERS_KEY } from '../CmdTable/constants';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import useEmployees from './useEmployees';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import { TEmployeeRecord, TEmployeeResponse } from './interfaces';
import { customersAtom, ICustomer, selectedCustomersAtom } from '../../store/recoil/customers';
import BulkOperations, { IBulkOperationAction } from '../CmdTable/BulkOperations';
import ActivateUser from './ActivateUser';
import BlockUser from './BlockUser';
import UnBlockUser from './UnBlockUser';
import ResetUser from './ResetBlockUser';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import { getTableSettings, handleOrderColumns } from '../CmdTable/CmdTableService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
  totalCountAtom,
} from '../CmdTable/CmdTableState';
import useAnalytics from '../../hooks/useAnalytics';
import LoaderService from '../../Services/LoaderService';
import ApiService from '../../Services/ApiService';
import PageHeader from '../PageHeader';

function Employee(): JSX.Element {
  const { formatMessage: f } = useIntl();
  dayjs.extend(weekOfYear);
  const { setDocumentTitle } = useDocumentTitle();
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_EMPLOYEE}/edit/:id`);
  const [itemId, setItemId] = React.useState<number | null>(null);
  const [isEdit, setIsEdit] = React.useState<boolean>(false);
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(false);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(EMPLOYEE_TABLE_NAME));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(EMPLOYEE_TABLE_NAME)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(EMPLOYEE_TABLE_NAME)
  );
  const { columns, columnOrder, columnVisibility } = useEmployees();
  const [selectedRows, setSelectedRows] = React.useState<TEmployeeRecord[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const tableCount = useRecoilValue<number>(totalCountAtom);

  const [isActivateConfirmDialogOpen, setIsActivateConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isDeactivateConfirmDialogOpen, setIsDeactivateConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isReactivateConfirmDialogOpen, setIsReactivateConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isResetConfirmDialogOpen, setIsResetConfirmDialogOpen] = React.useState<boolean>(false);
  const query = useUrlQueryParams();
  const { trackEvent } = useAnalytics();

  const updateTable = () => {
    // setCallTable(true);
    queryClient.invalidateQueries({
      queryKey: [EMPLOYEE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[],
    customerForSettings: ICustomer[]
  ) => {
    const orderColumns = handleOrderColumns(newOrderColumns, customerForSettings);

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(EMPLOYEE_TABLE_NAME);
    if (settings) {
      // setTableSettingsState(settings);
      // setSortFilter({ [`${settings.sort.column}`]: settings.sort.direction });
      handleSetOrderColumns(settings.columns, customer);
      // setIsSettingsReady(true);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['employeesCmdTableSettings'],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];

  const isLoading = isLoadingSettings;

  const handleClose = React.useCallback(() => {
    setIsSubpageOpened(false);
    setItemId(null);
    navigate(URL_EMPLOYEE);
    trackEvent(['employee', 'click', 'Close employee form']);
  }, [navigate, trackEvent]);

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`${URL_EMPLOYEE}/add`);
    trackEvent(['employee', 'click', 'Add employee']);
  };

  const handleRowChange = (data: TEmployeeRecord[], count: number) => {
    setSelectedRows(data);
    setTotalCount(count);
  };

  React.useEffect(() => {
    if (pathname === `${URL_EMPLOYEE}`) {
      setIsSubpageOpened(false);
      const documentTitle = `${f({ id: 'menu.employeeSettings' })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === `${URL_EMPLOYEE}/add`) {
      setIsSubpageOpened(true);
      setIsEdit(false);
      const documentTitle = `${f({ id: 'employee.add' })} - ${f({
        id: 'menu.employeeSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      setItemId(Number(params.id));
      setIsSubpageOpened(true);
      setIsEdit(true);
      const documentTitle = `${f({ id: 'employee.edit' })} - ${f({
        id: 'menu.employeeSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderForm = () => {
    const customerIdUrl = query.get('customerId');

    if (
      (PermissionsService.hasAccess(TABLE_PERSON, ['create'], selectedCustomers) && !itemId) ||
      (PermissionsService.hasAccess(TABLE_PERSON, ['update'], Number(customerIdUrl)) &&
        itemId &&
        typeof customerIdUrl === 'string')
    ) {
      return (
        <Form
          isEdited={isEdit}
          closeCallback={handleClose}
          editId={itemId}
          updateTable={updateTable}
        />
      );
    }
    if (
      // !PermissionsService.hasAccess(TABLE_PERSON, ['update'], selectedCustomers) &&
      PermissionsService.hasAccess(TABLE_PERSON, ['read'], Number(customerIdUrl)) &&
      itemId &&
      typeof customerIdUrl === 'string'
    ) {
      return (
        <div>
          <Detail id={itemId || 0} closeCallback={handleClose} />
        </div>
      );
    }
    return null;
  };

  const getSelectedRowsStatusValue = (selectedStatus: string[]) => {
    const filteredItems = selectedRows.filter((i) => selectedStatus.includes(i.status));
    return filteredItems.length === selectedRows.length;
  };

  const handleActivateConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsActivateConfirmDialogOpen(true);
  };

  const handleReactivateConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsReactivateConfirmDialogOpen(true);
  };

  const handleDeactivateConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsDeactivateConfirmDialogOpen(true);
  };

  const handleResetConfirmDialog = async (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    setIsResetConfirmDialogOpen(true);
  };

  const getAllowedMultipleActions = () => {
    const allSelectedRowsCustomerIds = selectedRows.map((i) => i.customer.id);
    const selectedRowsCustomerIds = [...new Set(allSelectedRowsCustomerIds)];
    const actions: IBulkOperationAction[] = [];
    if (
      PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], selectedRowsCustomerIds) &&
      getSelectedRowsStatusValue([IS_INACTIVE, IS_PENDING])
    ) {
      actions.push({
        id: 'activate',
        name: f({ id: 'table_list.actions.email' }),
        icon: ACTIVATE_USER,
        callback: handleActivateConfirmDialog,
        tooltip: f({ id: 'table_list.actions.email' }),
      });
    }
    if (
      PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], selectedRowsCustomerIds) &&
      getSelectedRowsStatusValue([IS_ACTIVE, IS_PENDING])
    ) {
      actions.push({
        id: 'deactivate',
        name: f({ id: 'table_list.actions.block' }),
        svgIcon: <BlockUserIcon style={{ marginRight: '8px' }} />,
        callback: handleDeactivateConfirmDialog,
        tooltip: f({ id: 'table_list.actions.block.tooltip' }),
      });
    }
    if (
      PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], selectedRowsCustomerIds) &&
      getSelectedRowsStatusValue([IS_BLOCKED])
    ) {
      actions.push({
        id: 'reactivate',
        name: f({ id: 'table_list.actions.unblock' }),
        colorIcon: REACTIVATE_USER,
        callback: handleReactivateConfirmDialog,
        tooltip: f({ id: 'table_list.actions.unblock.tooltip' }),
      });
    }
    if (
      PermissionsService.hasAccess(PERMISSION_PERSON, ['update'], selectedRowsCustomerIds) &&
      getSelectedRowsStatusValue([IS_ACTIVE])
    ) {
      actions.push({
        id: 'reset',
        name: f({ id: 'forgotPassword.passwordReset' }),
        icon: RESET_USER,
        callback: handleResetConfirmDialog,
        tooltip: f({ id: 'table_list.actions.reset' }),
      });
    }
    return actions;
  };

  const getSelectedIds = () => {
    return selectedRows.map((i) => i.userId);
  };

  const handleExport = async () => {
    LoaderService.showLoader(true);
    try {
      const payload = {
        offset: 0,
        filter: filterForRequest,
        limit: tableCount || 1000000,
        sort: sortFilter,
      };
      const fileName = `${f({ id: 'menu.employeeSettings' })}.xlsx`;

      const response = await ApiService.postDownload(
        `/api/v1/person/export/xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        payload
      );
      if (response) {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        LoaderService.showLoader(false);
      }
    } catch (error) {
      console.error('An error occurred while exporting:', error);
    }
  };

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'menu.settings' })} / ${f({ id: 'menu.employeeSettings' })}`}
        onlineHelpTopic="employees"
        show={!isSubpageOpened}
      />
      <div className="w-page mt-8">
        <div className="w-page-content">
          {isSubpageOpened ? (
            renderForm()
          ) : (
            <div>
              <div className="container-fluid">
                <section className="basic-box border-top-thin border-top-gray">
                  <div className="basic-box__content">
                    <div className="row no-gutters mb-2">
                      <div className="col-12 col-md-auto mr-md-2 mb-2">
                        {PermissionsService.hasAccess(
                          TABLE_PERSON,
                          ['create'],
                          selectedCustomers
                        ) && (
                          <a
                            href="#"
                            data-cy="table-record-addBtn"
                            title={f({ id: 'table.addBtn' })}
                            className="e-button e-button--gray"
                            data-id="addAlarm"
                            onClick={handleAddButton}
                          >
                            {f({ id: 'table.addBtn' })}
                          </a>
                        )}
                      </div>
                      <div className="col col-md" />
                      <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                      <div>
                        <button
                          className="e-button e-button--square px-2"
                          data-cy="cmd-table-export"
                          type="button"
                          onClick={() => handleExport()}
                        >
                          <i className="cmd-icons cmd-icon-download mr-2" />
                          {f({ id: 'table.export.button' })}
                        </button>
                      </div>
                      <div className="col-auto order-4 order-lg-5">
                        <CmdTableSettings
                          tableId={EMPLOYEE_TABLE_NAME}
                          tableName={EMPLOYEE_TABLE_SETTINGS_KEY}
                          sort={{ column: CUSTOMERS_KEY, direction: 'desc' }}
                          translationKeys={EmployeesTableTranslationKeys}
                        />
                      </div>
                    </div>
                    <div className="d-flex flex-wrap mb-2">
                      {tableFiltersKey &&
                        tableFiltersKey.map((filterId) => (
                          <FilterChips
                            key={filterId}
                            id={filterId}
                            tableName={EMPLOYEE_TABLE_NAME}
                          />
                        ))}
                    </div>
                    {selectedRows.length > 0 && (
                      <BulkOperations<TEmployeeRecord>
                        selectedRows={selectedRows}
                        actions={getAllowedMultipleActions()}
                        totalCount={totalCount}
                      />
                    )}
                    {isLoading && <CmdLoader inContent={true} />}
                    <CmdTable<TEmployeeResponse, TEmployeeRecord>
                      name={EMPLOYEE_TABLE_NAME}
                      // fetchSize={20}
                      stickyColumns={[ACTIONS_KEY]}
                      lockedColumns={[CHECKBOX_KEY, EMPLOYEE_CUSTOMER, EMPLOYEE_USERNAME]}
                      columns={columns}
                      columnOrder={columnOrder}
                      columnVisibility={columnVisibility}
                      onSelectedRowsChange={handleRowChange}
                      leftStickyColumn={[CHECKBOX_KEY]}
                    />
                  </div>
                </section>
              </div>
            </div>
          )}
          {isReactivateConfirmDialogOpen && (
            <UnBlockUser
              selectedIds={getSelectedIds()}
              handleConfirmDialogOpen={setIsReactivateConfirmDialogOpen}
              fullname={
                selectedRows.length === 1
                  ? `${selectedRows[0]?.firstName} ${selectedRows[0]?.lastName}`
                  : undefined
              }
              updateTable={updateTable}
            />
          )}
          {isActivateConfirmDialogOpen && (
            <ActivateUser
              selectedIds={getSelectedIds()}
              handleConfirmDialogOpen={setIsActivateConfirmDialogOpen}
              email={selectedRows.length === 1 ? selectedRows[0]?.email : undefined}
              updateTable={updateTable}
            />
          )}
          {isDeactivateConfirmDialogOpen && (
            <BlockUser
              selectedIds={getSelectedIds()}
              handleConfirmDialogOpen={setIsDeactivateConfirmDialogOpen}
              fullname={
                selectedRows.length === 1
                  ? `${selectedRows[0]?.firstName} ${selectedRows[0]?.lastName}`
                  : undefined
              }
              updateTable={updateTable}
            />
          )}
          {isResetConfirmDialogOpen && (
            <ResetUser
              selectedIds={getSelectedIds()}
              handleConfirmDialogOpen={setIsResetConfirmDialogOpen}
              fullname={
                selectedRows.length === 1
                  ? `${selectedRows[0]?.firstName} ${selectedRows[0]?.lastName}`
                  : undefined
              }
              updateTable={updateTable}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(Employee);
